import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import type { ChartTradeMarkerHover } from '../../types';
import type { TimeSeriesChartHover, TimeSeriesData } from '../types';
import type { TimeSeriesChartContext, TradeMarkerGroupGeometry } from '../utils/geometry';
import { findClosestTimeSeriesPoint, findTradeMarkerGroupAtX } from '../utils/geometry';

type UseTimeSeriesChartHoverOptions<T extends TimeSeriesData> = {
  context: TimeSeriesChartContext<T> | undefined;
  width: number;
  height: number;
  getHoverValue: (point: T) => number;
  onPointHover?: (hover: TimeSeriesChartHover<T> | undefined) => void;
  markerGeometry?: TradeMarkerGroupGeometry[];
  onMarkerHover?: (hover: ChartTradeMarkerHover | undefined) => void;
};

const NO_MARKER_GEOMETRY: TradeMarkerGroupGeometry[] = [];

export function useTimeSeriesChartHover<T extends TimeSeriesData>({
  context,
  width,
  height,
  getHoverValue,
  onPointHover,
  markerGeometry = NO_MARKER_GEOMETRY,
  onMarkerHover,
}: UseTimeSeriesChartHoverOptions<T>) {
  const hoveredPointRef = useRef<T | undefined>(undefined);
  const hoveredMarkerKeyRef = useRef<string | undefined>(undefined);
  const pointerXRef = useRef<number | undefined>(undefined);
  const [activeHover, setActiveHover] = useState<TimeSeriesChartHover<T>>();
  const [activeMarkerKey, setActiveMarkerKey] = useState<string>();

  const clearHover = () => {
    if (hoveredPointRef.current) {
      hoveredPointRef.current = undefined;
      setActiveHover(undefined);
      onPointHover?.(undefined);
    }
    if (hoveredMarkerKeyRef.current) {
      hoveredMarkerKeyRef.current = undefined;
      setActiveMarkerKey(undefined);
      onMarkerHover?.(undefined);
    }
    pointerXRef.current = undefined;
  };

  const handlePointerMove = (event: ReactPointerEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg || (!onPointHover && !onMarkerHover) || !context) return;

    const bounds = svg.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) return;

    const { data, plot, xDomain, xScale, yScale } = context;
    const svgX = ((event.clientX - bounds.left) / bounds.width) * width;
    const svgY = ((event.clientY - bounds.top) / bounds.height) * height;

    let hoveredGroup: TradeMarkerGroupGeometry | undefined;
    if (onMarkerHover) {
      pointerXRef.current = svgX;
      hoveredGroup = findTradeMarkerGroupAtX(markerGeometry, svgX);
      if (hoveredGroup?.key !== hoveredMarkerKeyRef.current) {
        hoveredMarkerKeyRef.current = hoveredGroup?.key;
        setActiveMarkerKey(hoveredGroup?.key);
        onMarkerHover(
          hoveredGroup
            ? {
                group: hoveredGroup,
                x: hoveredGroup.x,
                y: hoveredGroup.y,
                plot: context.plot,
              }
            : undefined,
        );
      }
    }

    if (!onPointHover) return;

    // While snapped to a marker, pin the point hover to the marker's anchor so the line
    // selection does not slide within the snap band while the tooltip stays fixed.
    const hoverX = hoveredGroup?.x ?? svgX;
    const pointerRatio = Math.min(Math.max((hoverX - plot.left) / plot.width, 0), 1);
    const [minTimestamp, maxTimestamp] = xDomain;
    const timestamp = minTimestamp + (maxTimestamp - minTimestamp) * pointerRatio;
    const point = findClosestTimeSeriesPoint(data, timestamp);

    if (!point) return;

    const hover = {
      point,
      x: xScale(point.timestamp),
      y: yScale(getHoverValue(point)),
      cursorY: Math.min(Math.max(svgY, plot.top), plot.bottom),
      plot,
    };
    hoveredPointRef.current = point;
    setActiveHover(hover);
    onPointHover(hover);
  };

  const handlePointerLeave = (event: ReactPointerEvent<SVGRectElement>) => {
    if (!event.pointerType || event.pointerType === 'mouse') clearHover();
  };

  const handlePointerEnd = (event: ReactPointerEvent<SVGRectElement>) => {
    if (event.pointerType !== 'mouse') clearHover();
  };

  // Geometry can move under a stationary pointer (resize, domain shift); re-run the hit test so
  // the emitted hover keeps fresh coordinates instead of the ones captured on the last move.
  useEffect(() => {
    const svgX = pointerXRef.current;
    if (!onMarkerHover || svgX == null || !hoveredMarkerKeyRef.current) return;

    const hoveredGroup = context ? findTradeMarkerGroupAtX(markerGeometry, svgX) : undefined;
    hoveredMarkerKeyRef.current = hoveredGroup?.key;
    setActiveMarkerKey(hoveredGroup?.key);
    onMarkerHover(
      hoveredGroup && context
        ? { group: hoveredGroup, x: hoveredGroup.x, y: hoveredGroup.y, plot: context.plot }
        : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- geometry/context changes are the trigger
  }, [markerGeometry, context]);

  const resolvedActiveHover =
    activeHover && context?.data.includes(activeHover.point) ? activeHover : undefined;
  const resolvedActiveMarkerKey =
    activeMarkerKey && markerGeometry.some(({ key }) => key === activeMarkerKey)
      ? activeMarkerKey
      : undefined;

  return {
    activeHover: resolvedActiveHover,
    activeMarkerKey: resolvedActiveMarkerKey,
    clearHover,
    handlePointerMove,
    handlePointerLeave,
    handlePointerEnd,
  };
}
